import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import {
  getChapter,
  getChapterImages,
  getComicById,
} from "@/lib/comicUtils";

export default function Read() {
  const params = useParams();
  const navigate = useNavigate();
  const comicId = params.comicId || "";
  const chapterId = params.chapterId || "";

  const comic = getComicById(comicId);
  const chapter = getChapter(comic, chapterId);

  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [fitWidth, setFitWidth] = useState(true);
  const touchStartX = useRef(0);

  const images = useMemo(
    () => (comic && chapter ? getChapterImages(comicId, chapterId) : []),
    [comicId, chapterId, comic, chapter]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!comic || !chapter) {
      setFailed(true);
      setLoading(false);
      return;
    }
    setFailed(false);
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 300);
    return () => clearTimeout(timer);
  }, [comicId, chapterId, comic, chapter]);

  useDocumentMeta({
    title: comic && chapter
      ? `${comic.title} ${chapter.name} - 在线阅读 - 歪歪漫画在线观看`
      : "在线阅读 - 歪歪漫画在线观看",
    description: comic
      ? `${comic.title} ${chapter?.name} 在线免费阅读，高清漫画图片连续展示，支持上一章下一章切换。`
      : "歪歪漫画在线观看在线阅读页。",
    keywords: comic ? `${comic.title},${comic.title}在线阅读,歪歪漫画` : "歪歪漫画在线观看",
  });

  const chapterIndex = comic
    ? comic.chapters.findIndex((c) => c.id === chapterId)
    : -1;
  const prevChapter =
    comic && chapterIndex > 0 ? comic.chapters[chapterIndex - 1] : null;
  const nextChapter =
    comic && chapterIndex >= 0 && chapterIndex < comic.chapters.length - 1
      ? comic.chapters[chapterIndex + 1]
      : null;

  const goPrev = () => {
    if (prevChapter) navigate(`/read/${comicId}/${prevChapter.id}`);
  };
  const goNext = () => {
    if (nextChapter) navigate(`/read/${comicId}/${nextChapter.id}`);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.changedTouches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (delta > 70) goPrev();
    else if (delta < -70) goNext();
  };

  const retry = () => {
    setFailed(false);
    setLoading(true);
    setTimeout(() => setLoading(false), 400);
  };

  return (
    <div className="min-h-screen bg-background-200">
      <header className="sticky top-0 z-40 w-full border-b border-background-300 bg-background-50/95 backdrop-blur">
        <div className="mx-auto flex w-full max-w-4xl items-center gap-2 px-3 py-3 md:px-6">
          <Link to="/" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-foreground-700 hover:bg-background-100" aria-label="返回首页">
            <i className="ri-home-4-line text-lg"></i>
          </Link>
          <div className="min-w-0 flex-1 text-center">
            <p className="line-clamp-1 text-sm font-medium text-foreground-900">
              {comic ? comic.title : "阅读"}
            </p>
            <p className="line-clamp-1 text-xs text-foreground-500">
              {chapter ? chapter.name : "章节加载中"}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setFitWidth((v) => !v)}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
            aria-label="切换缩放"
            title={fitWidth ? "原始尺寸" : "适应宽度"}
          >
            <i className={fitWidth ? "ri-zoom-in-line text-lg" : "ri-zoom-out-line text-lg"}></i>
          </button>
          <Link
            to={`/detail/${comicId}`}
            className="flex h-9 shrink-0 items-center gap-1 rounded-md bg-primary-500 px-3 text-sm text-background-50 hover:bg-primary-600 whitespace-nowrap"
          >
            <i className="ri-list-check-2"></i>
            <span className="hidden sm:inline">目录</span>
          </Link>
        </div>
      </header>

      <main className="mx-auto w-full max-w-4xl px-0 pb-24 md:px-6">
        {failed ? (
          <div className="flex flex-col items-center justify-center px-6 py-32 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-file-warning-line text-3xl"></i>
            </span>
            <h1 className="mt-4 font-heading text-xl text-foreground-900">章节加载失败或不存在</h1>
            <p className="mt-2 text-sm text-foreground-500">该章节可能已被移除，您可以重试或返回目录查看其他章节。</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <button
                type="button"
                onClick={retry}
                className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                重试
              </button>
              <Link
                to={`/detail/${comicId}`}
                className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm text-foreground-700 hover:border-primary-400 whitespace-nowrap"
              >
                返回目录
              </Link>
            </div>
          </div>
        ) : loading ? (
          <div className="flex flex-col items-center justify-center py-32">
            <span className="h-10 w-10 animate-spin rounded-full border-2 border-background-300 border-t-primary-500"></span>
            <p className="mt-4 text-sm text-foreground-500">漫画加载中…</p>
          </div>
        ) : (
          <div onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
            <div className="overflow-x-auto bg-background-300/60">
              <div className="flex flex-col items-center py-2">
                {images.map((src, idx) => (
                  <div
                    key={`${src}-${idx}`}
                    className={`bg-background-50 ${
                      fitWidth ? "w-full max-w-[820px]" : "w-[1100px] max-w-none"
                    }`}
                  >
                    <img
                      src={src}
                      alt={`${comic?.title} ${chapter?.name} 第${idx + 1}页`}
                      title={`${comic?.title}在线阅读`}
                      className="mx-auto block w-full"
                    />
                  </div>
                ))}
              </div>
            </div>
            <p className="py-4 text-center text-xs text-foreground-400">
              — 本章节结束 —
            </p>
          </div>
        )}
      </main>

      {!failed && !loading && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-background-300 bg-background-50/95 backdrop-blur">
          <div className="mx-auto flex w-full max-w-4xl items-center justify-between gap-3 px-4 py-3">
            <button
              type="button"
              onClick={goPrev}
              disabled={!prevChapter}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-md border border-background-300 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40 whitespace-nowrap cursor-pointer sm:flex-none sm:px-6"
            >
              <i className="ri-arrow-left-s-line text-lg"></i>
              上一章
            </button>
            <Link
              to={`/detail/${comicId}`}
              className="flex items-center justify-center gap-1.5 rounded-md px-4 py-2.5 text-sm text-foreground-600 hover:text-primary-600 whitespace-nowrap"
            >
              <i className="ri-list-check-2"></i>
              目录
            </Link>
            <button
              type="button"
              onClick={goNext}
              disabled={!nextChapter}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-primary-500 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-40 whitespace-nowrap cursor-pointer sm:flex-none sm:px-6"
            >
              下一章
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}